import { AlertTriangle, BellRing, CalendarClock, ChevronRight, PencilLine, CheckCheck } from 'lucide-react';
import type { ActionItem, ActionKind } from './metrics';
import { formatFullTL } from './metrics';
import type { SavedProposalDoc } from '../ProposalsModal';

const KIND_STYLES: Record<ActionKind, { icon: typeof BellRing; chip: string; badge: string; tag: string }> = {
  expired:      { icon: AlertTriangle,  chip: 'bg-red-50 text-red-600',        badge: 'bg-red-100 text-red-700',        tag: 'Geçerlilik doldu' },
  expiring:     { icon: CalendarClock,  chip: 'bg-amber-50 text-amber-600',    badge: 'bg-amber-100 text-amber-700',    tag: 'Süre doluyor' },
  stale_quoted: { icon: BellRing,       chip: 'bg-indigo-50 text-indigo-500',  badge: 'bg-indigo-100 text-indigo-700',  tag: 'Yanıt bekliyor' },
  stale_draft:  { icon: PencilLine,     chip: 'bg-stone-100 text-stone-500',   badge: 'bg-stone-200 text-stone-600',    tag: 'Yarım kaldı' },
};

function message(item: ActionItem): string {
  switch (item.kind) {
    case 'expired':
      return `Teklifin geçerlilik tarihi ${item.days} gün önce doldu, yenilemen gerekebilir.`;
    case 'expiring':
      return item.days === 0
        ? 'Teklifin geçerlilik süresi bugün doluyor.'
        : `Teklifin geçerlilik süresi ${item.days} gün sonra doluyor.`;
    case 'stale_quoted':
      return `${item.days} gündür müşteriden ses yok, hatırlatmak için iyi bir zaman.`;
    case 'stale_draft':
      return `${item.days} gündür taslak halinde bekliyor, tamamlayıp müşteriye iletebilirsin.`;
  }
}

export function ActionList({ items, onOpen }: { items: ActionItem[]; onOpen: (p: SavedProposalDoc) => void }) {
  if (items.length === 0) {
    return (
      <div className="px-5 py-10 flex flex-col items-center text-center gap-2">
        <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <CheckCheck className="w-5 h-5" />
        </div>
        <p className="text-xs font-bold text-stone-700">Bekleyen bir işin yok</p>
        <p className="text-[11px] text-stone-400 max-w-[280px] leading-relaxed">
          Geçerliliği dolan ya da uzun süredir dokunulmayan bir teklifin olduğunda burada listelenecek.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-stone-100">
      {items.map(item => {
        const style = KIND_STYLES[item.kind];
        const Icon = style.icon;
        const p = item.proposal;
        return (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onOpen(p)}
              className="w-full text-left px-5 py-3.5 flex items-center gap-3.5 hover:bg-stone-50 transition-colors group"
              title="Teklifi aç"
            >
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${style.chip}`}>
                <Icon className="w-4 h-4" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-stone-900 truncate">
                    {p.clientName || p.client?.clientName || 'İsimsiz Müşteri'}
                  </span>
                  <span className={`shrink-0 px-2 py-0.5 rounded text-[9.5px] font-medium tracking-wide ${style.badge}`}>
                    {style.tag}
                  </span>
                </span>
                <span className="mt-0.5 block text-[11px] text-stone-500 leading-relaxed">{message(item)}</span>
              </span>

              <span className="shrink-0 text-right hidden sm:block">
                <span className="block text-xs font-semibold text-stone-900 tabular-nums">{formatFullTL(p.totalAmount || 0)}</span>
              </span>
              <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 shrink-0 transition-colors" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
